import type { WsStatus } from '@shared/types'

import { useEffectQueue } from '@features/effect-queue'
import { useWsConnectionStore } from '@features/ws-connection'
import { fadeAnimate } from '@shared/lib/waapi'
import { useEffect, useRef } from 'react'

import styles from './HealthIndicator.module.css'

/**
 * Индикатор WS-соединения для OBS preview.
 * opacity: 0.18 на трансляции — виден только в панели источников OBS.
 * В dev-режиме (data-dev на <html>) — полная непрозрачность.
 *
 * React 19: использует ref-коллбэк вместо useCallback+useRef для dot-элемента.
 */
export function HealthIndicator() {
  const status    = useWsConnectionStore((s) => s.status)
  const lastPongAt = useWsConnectionStore((s) => s.lastPongAt)
  const pendingCount = useEffectQueue((s) => s.pending.length)
  const activeCount  = useEffectQueue((s) => s.active.length)

  const dotRef = useRef<HTMLSpanElement>(null)
  const prevStatus = useRef<WsStatus>(status)

  useEffect(() => {
    if (prevStatus.current === status) return
    prevStatus.current = status
    fadeAnimate({ element: dotRef.current, from: 0.2, to: 1, duration: 300 })
  }, [status])

  const label = STATUS_LABEL[status]
  const color = STATUS_COLOR[status]
  const secondsAgo = lastPongAt != null ? Math.round((Date.now() - lastPongAt) / 1000) : null

  return (
    <div className={styles.wrapper} role="status" aria-label={`WebSocket: ${label}`}>
      <span
        ref={dotRef}
        className={styles.dot}
        style={{ background: color }}
        data-connected={status === 'connected' ? 'true' : 'false'}
      />
      <span className={styles.text}>
        {label}
        {status === 'connected' && secondsAgo !== null && (
          <span className={styles.meta}> · pong {secondsAgo}s ago</span>
        )}
        {(activeCount > 0 || pendingCount > 0) && (
          <span className={styles.meta}> · {activeCount} active / {pendingCount} pending</span>
        )}
      </span>
    </div>
  )
}

const STATUS_LABEL: Record<WsStatus, string> = {
  connecting: 'connecting…', connected: 'live', disconnected: 'offline', error: 'error',
}
const STATUS_COLOR: Record<WsStatus, string> = {
  connecting: '#ff9f1c', connected: '#2ec4b6', disconnected: '#888', error: '#ff4d6d',
}
